/**
 * Traction's calendar and number helpers: quarters ("Q4-2026"), scorecard weeks
 * (each is its Monday), a scorecard number in its unit, and the V/TO's lists.
 * Days are YYYY-MM-DD on the agency's calendar; the page works out today with
 * AGENCY_TZ and passes it in. No imports and only erasable types, so node --test
 * loads this file directly (test/traction.test.js).
 */

const QUARTER = /^Q([1-4])-(\d{4})$/;

/** The quarter a day falls in: "2026-10-03" → "Q4-2026". */
export function quarterOf(day: string): string {
  const [year, month] = day.split("-").map(Number);
  return `Q${Math.floor((month - 1) / 3) + 1}-${year}`;
}

/** n quarters later (or earlier, for a negative n): ("Q4-2026", 1) → "Q1-2027". */
export function shiftQuarter(quarter: string, n: number): string {
  const match = QUARTER.exec(quarter);
  if (!match) return quarter;
  const index = Number(match[2]) * 4 + Number(match[1]) - 1 + n;
  return `Q${(index % 4) + 1}-${Math.floor(index / 4)}`;
}

/** "Q4-2026" → "Q4 2026". */
export const quarterLabel = (quarter: string) => quarter.replace("-", " ");

const quarterIndex = (quarter: string) => {
  const match = QUARTER.exec(quarter);
  return match ? Number(match[2]) * 4 + Number(match[1]) : -1;
};

/** A rock can be planned for last quarter, this one or the next two. */
export function quarterChoices(current: string): string[] {
  return [-1, 0, 1, 2].map((n) => shiftQuarter(current, n));
}

/** Every quarter worth listing: the planning window plus any that hold rocks, oldest first. */
export function quarterFilterChoices(current: string, withRocks: readonly string[] = []): string[] {
  const all = new Set([...quarterChoices(current), ...withRocks.filter((q) => QUARTER.test(q))]);
  return [...all].sort((a, b) => quarterIndex(a) - quarterIndex(b));
}

const utc = (day: string) => new Date(`${day}T12:00:00Z`);
const ymd = (date: Date) => date.toISOString().slice(0, 10);

/** The Monday a scorecard week is filed under. */
export function weekStart(day: string): string {
  const date = utc(day);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return ymd(date);
}

/** The last `count` scorecard weeks, oldest first, ending with the week holding `today`. */
export function recentWeeks(today: string, count = 6): string[] {
  const last = utc(weekStart(today));
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(last);
    date.setUTCDate(last.getUTCDate() - 7 * (count - 1 - i));
    return ymd(date);
  });
}

/** "2026-09-28" → "Sep 28". Anything that is not a date is shown as given. */
export function weekLabel(monday: string): string {
  const date = utc(String(monday).slice(0, 10));
  if (Number.isNaN(date.getTime())) return String(monday ?? "");
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}

/** A scorecard number in its unit: "$15,000", "85%", "2.8×", "7.5", "12". */
export function formatMetric(value: unknown, unit?: string | null): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const plain = n.toLocaleString("en-US", { maximumFractionDigits: unit === "$" ? 0 : 2 });
  if (unit === "$") return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (unit === "%") return `${plain}%`;
  if (unit === "×" || unit === "x") return `${n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}×`;
  return plain;
}

/** The goal with its direction: "≥ $15,000", "≤ 5". */
export function goalLabel(goal: unknown, direction?: string | null, unit?: string | null): string {
  const sign = ({ ">=": "≥", "<=": "≤", ">": ">", "=": "=" } as Record<string, string>)[direction || ""] || "";
  return `${sign ? `${sign} ` : ""}${formatMetric(goal, unit)}`;
}

export type CoreValue = { value: string; description?: string; behaviors?: unknown[] };

/** One line per value, "Value: what it means". */
export function formatCoreValues(values: unknown): string {
  if (!Array.isArray(values)) return "";
  return values
    .filter((v): v is CoreValue => Boolean(v) && typeof v === "object" && typeof (v as CoreValue).value === "string")
    .map((v) => (v.description ? `${v.value}: ${v.description}` : v.value))
    .join("\n");
}

/**
 * Lines back into core values. The name is before the first colon. A value that
 * already had behaviors keeps them, so editing the wording never loses them.
 */
export function parseCoreValues(text: string, existing: unknown = []): CoreValue[] {
  const before = new Map(
    (Array.isArray(existing) ? existing : [])
      .filter((v): v is CoreValue => Boolean(v) && typeof v === "object" && typeof (v as CoreValue).value === "string")
      .map((v) => [v.value.trim().toLowerCase(), v]),
  );
  return parseLines(text).map((line) => {
    const colon = line.indexOf(":");
    const value = (colon > 0 ? line.slice(0, colon) : line).trim();
    const description = colon > 0 ? line.slice(colon + 1).trim() : "";
    const behaviors = before.get(value.toLowerCase())?.behaviors;
    return { value, description, behaviors: Array.isArray(behaviors) ? behaviors : [] };
  });
}

/** One item per line; blank lines and stray bullets dropped. */
export function parseLines(text: string): string[] {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "").trim())
    .filter(Boolean);
}

/**
 * A stored V/TO list as readable lines, whatever shape it was saved in: lines,
 * objects ({ goal: "..." }) or a single answer.
 */
export function listItems(value: unknown): string[] {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) return value.flatMap((item) => listItems(item));
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => {
        const text = typeof v === "object" ? listItems(v).join(", ") : String(v);
        return ["goal", "text", "item", "value", "description"].includes(k) ? text : `${k.charAt(0).toUpperCase()}${k.slice(1).replaceAll("_", " ")}: ${text}`;
      });
  }
  return [String(value).trim()].filter(Boolean);
}
